import type { ReminderListRequest } from '@ghp/api-client'
import type { ReminderReasonFilter } from '@ghp/domain'
import { z } from 'zod'

import { urlCount } from '../../../lib/search-params'

/**
 * Le modèle de l'écran « Relances » : le schéma des `search params`, les trois vues, et la
 * **couture** vers la requête serveur. Hors de la route pour que le composant d'écran reste du
 * markup — et parce que la déclaration de route importe ce module, alors que l'écran, lui, est
 * chargé à la demande.
 *
 * ⚠️ Même discipline que « Demandes » et « Export clients » : l'URL **est** l'état, chaque champ
 * porte un `.catch` (M21 appliqué à l'URL), et **rien n'est filtré en mémoire** (D24).
 */

/**
 * Les trois vues. Chacune est un **préréglage du filtre serveur** `reason`, pas un concept de
 * plus (M14 § 1).
 *
 * ⚠️ **« Toutes » est la vue par défaut, et les deux motifs s'y recouvrent** : une demande dont
 * le rendez-vous a été manqué *et* dont le délai est dépassé y figure une fois, pas deux. C'est
 * pour cela que les effectifs des deux autres vues, additionnés, dépassent parfois celui-ci.
 */
export const REMINDER_VIEWS = [
  { value: 'all', label: 'Toutes' },
  { value: 'missed_appointment', label: 'RDV non honorés' },
  { value: 'past_deadline', label: 'Hors délai réglementaire' },
  { value: 'late_appointment', label: 'RDV à prendre' },
] as const satisfies readonly { value: ReminderReasonFilter; label: string }[]

const REMINDER_VIEW_VALUES = REMINDER_VIEWS.map(({ value }) => value) as [
  ReminderReasonFilter,
  ...ReminderReasonFilter[],
]

export const reminderSearchSchema = z.object({
  reason: z.enum(REMINDER_VIEW_VALUES).default('all').catch('all'),
  offset: urlCount(0),
})

export type ReminderSearch = z.infer<typeof reminderSearchSchema>

/** Les valeurs par défaut du schéma, partagées entre l'URL et la remise à zéro de la tranche. */
export const REMINDER_DEFAULTS: ReminderSearch = reminderSearchSchema.parse({})

/** Combien de lignes une page demande — même densité que les autres tableaux du bureau. */
export const REMINDER_PAGE_SIZE = 50

/**
 * **La couture** : l'état d'URL de l'écran vers ce que l'API accepte (D21).
 *
 * ⚠️ Rien d'autre ne passe, et surtout pas un « maintenant » : c'est le serveur qui date la
 * lecture. Le lui envoyer laisserait une horloge de poste décider de ce qui est en retard.
 */
export function toReminderQuery(search: ReminderSearch): ReminderListRequest {
  return {
    reason: search.reason,
    limit: REMINDER_PAGE_SIZE,
    offset: search.offset,
  }
}
